import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, RotateCcw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { StatusPill, STATUS_LABELS } from "@/components/Status";
import ConfirmDialog from "@/components/ConfirmDialog";
import { ORDER, TRASH } from "@/constants/testIds";

const STATUS_FILTERS = ["all", "requested", "researching", "placed_with_vendor", "receiving", "ready", "closed", "cancelled"];

export default function OrderHistoryPage() {
  const [tab, setTab] = useState("active"); // 'active' | 'deleted'
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(null); // {action:'delete'|'restore', order}
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { params: { deleted: tab === "deleted" } };
      if (tab === "active" && filter !== "all") params.params.status = filter;
      const { data } = await api.get("/orders", params);
      setOrders(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [tab, filter]);

  useEffect(() => { load(); }, [load]);

  const doConfirm = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      if (pending.action === "delete") {
        await api.delete(`/orders/${pending.order.order_id}`);
      } else {
        await api.post(`/orders/${pending.order.order_id}/restore`);
      }
      setPending(null);
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setPending(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-12">
        <div className="overline">Members&rsquo; Ledger</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-2 leading-none">
          Order History
        </h1>

        {/* Tab toggle: Active / Deleted */}
        <div className="mt-8 inline-flex border border-[#E5E3D8] bg-white">
          <button
            type="button"
            onClick={() => setTab("active")}
            data-testid={TRASH.tabActive}
            className={`px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold transition-colors ${
              tab === "active"
                ? "bg-[#1A422D] text-[#F9F8F4]"
                : "text-[#1A422D] hover:bg-[#F3F1E9]"
            }`}
          >
            Active Orders
          </button>
          <button
            type="button"
            onClick={() => setTab("deleted")}
            data-testid={TRASH.tabDeleted}
            className={`px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold transition-colors border-l border-[#E5E3D8] ${
              tab === "deleted"
                ? "bg-[#8a1a1a] text-[#F9F8F4]"
                : "text-[#8a1a1a] hover:bg-[#fbe9e9]"
            }`}
          >
            Deleted
          </button>
        </div>

        {tab === "deleted" && (
          <p className="text-xs text-[#7A7A7A] mt-3 max-w-xl">
            Deleted orders are kept for 5 days, then removed permanently. Use the
            Restore button to bring one back.
          </p>
        )}

        {tab === "active" && (
          <div className="mt-6 flex flex-wrap gap-2">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setFilter(s)}
                className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${
                  filter === s
                    ? "bg-[#1A422D] text-white border-[#1A422D]"
                    : "bg-white text-[#1A422D] border-[#E5E3D8] hover:border-[#1A422D]"
                }`}
              >
                {s === "all" ? "All" : STATUS_LABELS[s]}
              </button>
            ))}
          </div>
        )}

        {error && (
          <div className="mt-6 px-4 py-3 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]">
            {error}
          </div>
        )}

        <div className="mt-6 surface-card overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-[#F3F1E9]">
              <tr className="text-xs uppercase tracking-wider text-[#4A4A4A]">
                <th className="px-6 py-4">Item</th>
                <th className="px-6 py-4">Details</th>
                <th className="px-6 py-4">{tab === "deleted" ? "Deleted" : "Submitted"}</th>
                <th className="px-6 py-4">{tab === "deleted" ? "Permanent in" : "Status"}</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-6 py-8 text-center text-[#7A7A7A]">Loading…</td></tr>
              )}
              {!loading && orders.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="overline">{tab === "deleted" ? "Trash is Empty" : "Empty Ledger"}</div>
                    <div className="font-serif text-2xl text-[#1A1A1A] mt-2">
                      {tab === "deleted" ? "Nothing here." : "No orders to show."}
                    </div>
                    {tab === "active" && (
                      <Link to="/orders/new" className="btn-primary inline-block mt-4">Place an Order</Link>
                    )}
                  </td>
                </tr>
              )}
              {!loading && orders.map((o) => (
                <tr
                  key={o.order_id}
                  data-testid={`${ORDER.rowPrefix}${o.order_id}`}
                  className="border-t border-[#E5E3D8] hover:bg-[#FAF9F2]"
                >
                  <td className="px-6 py-4">
                    <div className="font-serif text-lg text-[#1A1A1A]">{o.item_name}</div>
                    <div className="text-xs text-[#7A7A7A] uppercase tracking-wider">{o.category || "—"}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-[#4A4A4A]">
                    {[o.color, o.size, `Qty ${o.quantity}`].filter(Boolean).join(" · ")}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#4A4A4A]">
                    {new Date(tab === "deleted" ? o.deleted_at : o.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    {tab === "deleted" ? (
                      <span
                        data-testid={`${TRASH.countdownPrefix}${o.order_id}`}
                        className="inline-block px-3 py-1 text-xs uppercase tracking-wider border border-[#8a1a1a] text-[#8a1a1a] bg-[#fbe9e9]"
                      >
                        {o.deletes_in_days > 0
                          ? `${o.deletes_in_days} day${o.deletes_in_days === 1 ? "" : "s"}`
                          : "Today"}
                      </span>
                    ) : (
                      <StatusPill status={o.status} />
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {tab === "deleted" ? (
                        <button
                          type="button"
                          onClick={() => setPending({ action: "restore", order: o })}
                          data-testid={`${TRASH.restorePrefix}${o.order_id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs uppercase tracking-wider text-[#1A422D] border border-[#1A422D] hover:bg-[#E9F0EB] transition-colors"
                          aria-label="Restore order"
                        >
                          <RotateCcw size={14} /> Restore
                        </button>
                      ) : (
                        <>
                          <Link to={`/orders/${o.order_id}`} className="text-sm text-[#1A422D] underline underline-offset-4">
                            View
                          </Link>
                          <button
                            type="button"
                            onClick={() => setPending({ action: "delete", order: o })}
                            data-testid={`${TRASH.deletePrefix}${o.order_id}`}
                            className="inline-flex items-center justify-center p-2 text-[#7A7A7A] hover:text-[#8a1a1a] hover:bg-[#fbe9e9] transition-colors"
                            aria-label="Delete order"
                            title="Delete this order"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        open={!!pending}
        tone={pending?.action === "delete" ? "danger" : "default"}
        title={
          pending?.action === "delete"
            ? `Delete "${pending.order.item_name}"?`
            : `Restore "${pending?.order?.item_name}"?`
        }
        message={
          pending?.action === "delete"
            ? "We'll move it to the Deleted tab. You'll have 5 days to restore it before it's removed permanently."
            : "The order will return to your active ledger."
        }
        confirmLabel={pending?.action === "delete" ? "Delete Order" : "Restore Order"}
        onConfirm={doConfirm}
        onCancel={() => setPending(null)}
        busy={busy}
      />
    </div>
  );
}
